import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { formatDate } from '@/shared/format';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import { describeAgreementFailure } from '@/modules/agreements/shared';
import { Field } from './execution-summary';

/**
 * The RESTRICTED evidence package.
 *
 * It carries the observed network and device facts recorded at signing. It is fetched on
 * demand rather than with the page, so a caller without `platform.agreement.evidence.read`
 * never receives it — the request is refused, and the refusal is shown honestly.
 */
export function EvidenceSection(props: { executionId: string }): ReactNode {
  const [evidence, setEvidence] = useState<Awaited<
    ReturnType<typeof agreementsPlatformApi.exportEvidence>
  > | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setEvidence(await agreementsPlatformApi.exportEvidence(props.executionId));
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-content-muted">
        The evidence package records the facts observed at signing. It is restricted to callers
        holding the evidence-read grant.
      </p>
      {error ? (
        <p className="text-sm text-critical">{describeAgreementFailure(error)}</p>
      ) : null}
      {evidence ? (
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Field label="Observed IP" value={evidence.evidence?.observedIp ?? '—'} mono />
          <Field label="Device category" value={evidence.evidence?.deviceCategory ?? '—'} />
          <Field label="Location state" value={evidence.evidence?.locationState ?? '—'} />
          <Field
            label="Recorded"
            value={
              evidence.evidence?.recordedAt ? formatDate(evidence.evidence.recordedAt) : '—'
            }
          />
        </dl>
      ) : (
        <Button loading={busy} onClick={() => void load()}>
          Load evidence package
        </Button>
      )}
    </div>
  );
}
